import React, { useState, useEffect } from 'react';
import { Spinner } from "react-bootstrap";
import axios from 'axios';
import Swal from "sweetalert2";
import { baseUrl } from '../components/BaseUrl';
import { MyProSidebarProvider } from "../components/SidebarContext";
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../App.css';

// Modern Icons
import { 
  FaCheck, 
  FaTimes, 
  FaCrown, 
  FaRocket, 
  FaShieldAlt, 
  FaClock, 
  FaUsers,
  FaStar,
  FaAward,
  FaBolt,
  FaGem,
  FaMedal,
  FaFire,
  FaTrendingUp,
  FaUserCheck,
  FaHeadset,
  FaGraduationCap,
  FaLaptopCode,
  FaInfinity
} from 'react-icons/fa';
import { 
  HiSparkles, 
  HiCheckBadge, 
  HiClock, 
  HiUserGroup 
} from 'react-icons/hi2';

// Environment variables
const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://your-api-url.com';
const PAYSTACK_URL = process.env.REACT_APP_PAYSTACK_URL || 'https://api.paystack.co';
const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || 'pk_test_your_public_key';

const SubscriptionPlans = () => {
  const [loading, setLoading] = useState(false);
  const [loadingMonthly, setMonthlyLoading] = useState(false);
  const [loadingFourMonths, setFourMonthsLoading] = useState(false);
  const [loadingSixMonths, setSixMonthsLoading] = useState(false);
  const [loadingYearly, setYearlyLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [secretKey, setSecretKey] = useState('');

  // Check if user is logged in and redirect if necessary
  useEffect(() => {
    checkLoginStatus();
    fetchSecretKey();
    checkEmailVerification();
  }, []);

  const checkLoginStatus = () => {
    const token = localStorage.getItem("codefussion-login-token");
    const loggedIn = token !== null;
    setIsLoggedIn(loggedIn);

    if (loggedIn) {
      console.log("User is logged in, staying on subscription page");
    }
  };

  // Fetch plans from database
  useEffect(() => {
    fetchPlans();
  }, [isLoggedIn]);

  const fetchPlans = async () => {
    try {
      setPlansLoading(true);
      const response = await fetch(`${baseUrl}get-subscriptions-plans.php`);
      // console.log(response)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(data)
      
      if (data.status === 1) {
        setPlans(data.data);
      } else {
        console.error("Failed to fetch plans:", data);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to load subscription plans. Please try again later.",
        icon: "error",
        confirmButtonText: "OK"
      });
    } finally {
      setPlansLoading(false);
    }
  };

  const checkEmailVerification = async () => {
    try {
        setLoading(true);
        const userEmail = localStorage.getItem("codefussion-student-email");
        
        if (!userEmail) {
            throw new Error('User email not found');
        }

        const response = await fetch(`${baseUrl}check-email-verification.php`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email: userEmail
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        data.map((item) => {
          setStatus(Number(item.verify_status))
        })
    } catch (error) {
        console.error('Error checking email verification:', error);
        Swal.fire({
            title: "Verification Error",
            text: "Failed to verify email status. Please try again.",
            icon: "error",
            confirmButtonText: "OK",
        });
    } finally {
        setLoading(false);
    }
  };

  const fetchSecretKey = async () => {
    try {
        setLoading(true);
        const token = localStorage.getItem("codefussion-login-token");
        if (!token) {
            throw new Error('No authentication token found');
        }

        const response = await axios.get(`${baseUrl}get_secret_key.php`, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            }
        });
        
        if (response.data.status === 1) {
            setSecretKey(response.data.secret_key);
            await checkEmailVerification();
        } else {
            throw new Error(response.data.message || 'Failed to get secret key');
        }
    } catch (error) {
        console.error('Error fetching secret key:', error);
        Swal.fire({
            title: "Security Error",
            text: "Failed to initialize security settings. Please try logging in again.",
            icon: "error",
            confirmButtonText: "OK",
        }).then(() => {
            window.location.replace("/login");
        });
    } finally {
        setLoading(false);
    }
  };

  const handleSubscription = async (plan) => {
    if (!isLoggedIn) {
      const currentPath = window.location.pathname;
      localStorage.setItem('codefussion-redirect-after-login', currentPath);
      window.location.replace("/login");
      return;
    }

    if (status != 1) {
      Swal.fire({
        title: "Email Not Verified",
        text: "Please verify your email to continue with subscription",
        icon: "error",
        confirmButtonText: "OK",
        backdrop: `rgba(143, 70, 0, 0.3)`
      });
      return;
    }

    setSelectedPlan(plan.id);
    
    const loadingSetters = {
      'monthly': setMonthlyLoading,
      'four-months': setFourMonthsLoading,
      'six-months': setSixMonthsLoading,
      'yearly': setYearlyLoading
    };
    
    if (loadingSetters[plan.id]) {
      loadingSetters[plan.id](true);
    }

    try {
      const userData = {
        email: localStorage.getItem("codefussion-student-email"),
        amount: plan.price * 100,
        plan: plan.planCode,
        callback_url: `${window.location.origin}/check-subscription`
      };

      const response = await axios.post(`${PAYSTACK_URL}/transaction/initialize`, userData, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${secretKey}`
        }
      });

      if (response.data.status && response.data.data.authorization_url) {
        localStorage.setItem("codefussion-payment-reference", response.data.data.reference);
        localStorage.setItem("codefussion-payment-code", response.data.data.access_code);
        window.location.href = response.data.data.authorization_url;
      }
    } catch (error) {
      console.error("Subscription error:", error);
      Swal.fire({
        title: "Error",
        text: "An error occurred while processing your subscription. Please try again.",
        icon: "error",
        confirmButtonText: "OK"
      });
    } finally {
      if (loadingSetters[plan.id]) {
        loadingSetters[plan.id](false);
      }
      setSelectedPlan(null);
    }
  };

  const calculateEffectivePrice = (plan) => {
    if (plan.id === 'monthly') return `₦${plan.price.toLocaleString()} per month`;
    if (plan.id === 'four-months') return `Only ₦${(plan.price / 4).toLocaleString()} per month`;
    if (plan.id === 'six-months') return `Only ₦${(plan.price / 6).toLocaleString()} per month`;
    if (plan.id === 'yearly') return `Only ₦${(plan.price / 12).toLocaleString()} per month`;
    return '';
  };

  const handleLoginRedirect = () => {
    if (isLoggedIn) {
      return;
    }
  };

  useEffect(() => {
    handleLoginRedirect();
  }, [isLoggedIn]);

  // Function to render feature icons based on feature content
  const renderFeatureIcon = (feature) => {
    if (feature.startsWith('FaTimes')) {
      return <FaTimes className="feature-icon disabled" />;
    }
    
    // Special icons for specific features
    if (feature.toLowerCase().includes('unlimited')) {
      return <FaInfinity className="feature-icon premium" />;
    }
    if (feature.toLowerCase().includes('premium')) {
      return <FaCrown className="feature-icon premium" />;
    }
    if (feature.toLowerCase().includes('support')) {
      return <FaHeadset className="feature-icon" />;
    }
    if (feature.toLowerCase().includes('certificate')) {
      return <FaAward className="feature-icon" />;
    }
    if (feature.toLowerCase().includes('access') || feature.toLowerCase().includes('courses')) {
      return <FaLaptopCode className="feature-icon" />;
    }
    if (feature.toLowerCase().includes('priority')) {
      return <FaBolt className="feature-icon" />;
    }
    
    // Default check icon
    return <FaCheck className="feature-icon" />;
  };

  // Function to get plan icon based on plan type
  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'monthly':
        return <HiSparkles className="plan-type-icon" />;
      case 'four-months':
        return <FaGem className="plan-type-icon" />;
      case 'six-months':
        return <FaMedal className="plan-type-icon" />;
      case 'yearly':
        return <FaCrown className="plan-type-icon" />;
      default:
        return <FaStar className="plan-type-icon" />;
    }
  };

  // Main content that will be wrapped conditionally
  const mainContent = (
    <div className="premium-subscription-page">
      
      {!isLoggedIn && <Navbar />}
      
      {/* Hero Section */}
      <section className="subscription-hero">
        <div className="hero-content">
          <div className="hero-badge">
            <FaRocket className="badge-icon" />
            Premium Learning Experience
          </div>
          <h1>Unlock Your <span className="gradient-text">Full Potential</span></h1>
          <p className="hero-subtitle">
            Join thousands of successful learners who transformed their careers. 
            <strong> Your journey to mastery starts now!</strong>
          </p>
          <div className="hero-stats">
            <div className="stat">
              <div className="stat-icon">
                <HiUserGroup />
              </div>
              <div className="stat-number">2,500+</div>
              <div className="stat-label">Successful Students</div>
            </div>
            <div className="stat">
              <div className="stat-icon">
                <FaStar />
              </div>
              <div className="stat-number">98%</div>
              <div className="stat-label">Satisfaction Rate</div>
            </div>
            <div className="stat">
              <div className="stat-icon">
                <HiClock />
              </div>
              <div className="stat-number">24/7</div>
              <div className="stat-label">Expert Support</div>
            </div>
          </div>
        </div>
      </section>

    <section>
     <div className="section-header">
            <h2>Find a plan that fits your goals</h2>
            <p>Flexible options designed to fit your goals and budget</p>
          </div>
          </section>
          

      <section className="plans-section">
        <div className="container">
          <div className="plans-comparison">
            {plansLoading ? (
              <div className="centered-loading-container">
                <div className="loading-content">
                  <Spinner animation="border" variant="primary" />
                  <p>Loading subscription plans...</p>
                </div>
              </div>
            ) : plans.length === 0 ? (
              <div className="no-plans-message">
                <FaGraduationCap className="no-plans-icon" />
                <h3>No subscription plans available at the moment</h3>
                <p>Please check back later or contact support.</p>
              </div>
            ) : (
              <div className="plans-container">
                {plans.map(plan => (
                  <div 
                    key={plan.id} 
                    className={`plan-card ${plan.popular ? 'popular featured' : ''}`}
                  >
                    {plan.popular && (
                      <div className="popular-badge">
                        <FaFire className="badge-icon" />
                        <span>MOST POPULAR</span>
                      </div>
                    )}

                    {plan.savings > 0 && (
                      <div className="savings-badge">
                        <FaBolt className="badge-icon" />
                        Save {plan.savings}%
                      </div>
                    )}

                    <div className="plan-header">
                      <div className="plan-type">
                        {getPlanIcon(plan.id)}
                        <h3>{plan.name}</h3>
                      </div>
                      <div className="price-section">
                        <div className="price">
                          <span className="currency">₦</span>
                          <span className="amount">{plan.price.toLocaleString()}</span>
                          <span className="period">/{plan.period}</span>
                        </div>
                        {plan.originalPrice > plan.price && (
                          <div className="original-price">
                            ₦{plan.originalPrice.toLocaleString()}
                          </div>
                        )}
                        <div className="effective-price">
                          {calculateEffectivePrice(plan)}
                        </div>
                      </div>
                    </div>

                    <div className="plan-features">
                      <ul>
                        {plan.features.map((feature, index) => (
                          <li key={index} className={feature.startsWith('FaTimes') ? 'disabled' : ''}>
                            {renderFeatureIcon(feature)}
                            <span className="feature-text">
                              {feature.replace('FaCheck ', '').replace('FaTimes ', '')}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="plan-action">
                      <button 
                        onClick={() => handleSubscription(plan)}
                        disabled={
                          (plan.id === 'monthly' && loadingMonthly) ||
                          (plan.id === 'four-months' && loadingFourMonths) ||
                          (plan.id === 'six-months' && loadingSixMonths) ||
                          (plan.id === 'yearly' && loadingYearly)
                        }
                        className={`subscribe-btn ${plan.popular ? 'btn-featured' : ''}`}
                      >
                        {selectedPlan === plan.id ? (
                          <>
                            <Spinner animation="border" size="sm" />
                            <span>Processing...</span>
                          </>
                        ) : (
                          <>
                            {plan.cta}
                            <span className="btn-arrow">→</span>
                          </>
                        )}
                      </button>
                      <p className="plan-guarantee">
                        <FaShieldAlt className="guarantee-icon" />
                        7-day money-back guarantee • Cancel anytime
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
       <div className="section-header">
            <h2>Why Choose Codeflare?</h2>
            <p className="text-dark">Experience learning that transforms your career</p>
          </div>
      <section className="value-proposition">
        <div className="container">
         
          <div className="value-grid">
            <div className="value-item">
              <div className="value-icon">
                <HiCheckBadge />
              </div>
              <h4>Industry Experts</h4>
              <p>Learn from professionals with real-world experience and proven track records</p>
            </div>
            <div className="value-item">
              <div className="value-icon">
                <FaUserCheck />
              </div>
              <h4>Personalized Support</h4>
              <p>Get dedicated mentorship and 1:1 guidance throughout your learning journey</p>
            </div>
            <div className="value-item">
              <div className="value-icon">
                <FaGraduationCap />
              </div>
              <h4>Career Advancement</h4>
              <p>Join our alumni network and get access to exclusive job opportunities</p>
            </div>
            <div className="value-item">
              <div className="value-icon">
                <FaLaptopCode />
              </div>
              <h4>Hands-on Projects</h4>
              <p>Build real projects that showcase your skills to potential employers</p>
            </div>
          </div>
        </div>
      </section>

      {/* Conditionally render footer */}
      {!isLoggedIn && <Footer />}

      {/* Updated CSS styles with modern icon styling */}
      <style jsx>{`
        .premium-subscription-page {
          // background: linear-gradient(135deg, #0066ff 0%, #0044cc 100%);
         background: #f8f9fa;
          min-height: 100vh;
        }

        .subscription-hero {
          // background: linear-gradient(135deg, #333 0%, #333 100%);
          backdrop-filter: blur(10px);
           background: linear-gradient(135deg, #0066ff 0%, #0044cc 100%);
          padding: 100px 0 80px;
          text-align: center;
          color: white;
        }

        .hero-content {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .hero-badge {
          // background: linear-gradient(45deg, #0066ff, #0044cc);
          background: linear-gradient(45deg, #010c4f, #010c4f);
          padding: 12px 25px;
          border-radius: 25px;
          font-weight: 700;
          font-size: 0.9rem;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 30px;
          animation: pulse 2s infinite;
        }

        .badge-icon {
          font-size: 1rem;
        }

        .subscription-hero h1 {
          font-size: 3.5rem;
          font-weight: 800;
          margin-bottom: 20px;
          line-height: 1.1;
        }

        .gradient-text {
          background: linear-gradient(45deg, #ffbf00, #ffbf00);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-subtitle {
          font-size: 1.3rem;
          margin-bottom: 40px;
          opacity: 0.9;
          line-height: 1.6;
        }

        .hero-stats {
          display: flex;
          justify-content: center;
          gap: 60px;
          margin-top: 50px;
        }

        .stat {
          text-align: center;
        }

        .stat-icon {
          font-size: 2rem;
          color: #ffbf00;
          margin-bottom: 10px;
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: 800;
          color: #ffffff;
          margin-bottom: 5px;
        }

        .stat-label {
          font-size: 0.9rem;
          opacity: 0.8;
        }

        .plans-section {
          padding: 60px 0 100px;
          background: white;
          min-height: 400px;
          display: flex;
          align-items: center;
        }

        .container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 20px;
          width: 100%;
        }

        .section-header {
          text-align: center;
          // margin-bottom: 60px;
          background: #f8f9fa;
        }

        .section-header h2 {
          font-size: 2.5rem;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 15px;
        }

        .section-header p {
          font-size: 1.2rem;
          color: #666;
          max-width: 600px;
          margin: 0 auto;
        }

        .plans-comparison {
          margin-bottom: 80px;
          width: 100%;
        }

        .plans-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 25px;
        }

        .centered-loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 300px;
          width: 100%;
        }

        .loading-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        .loading-content p {
          margin-top: 20px;
          font-size: 1.2rem;
          color: #666;
          font-weight: 500;
        }

        .no-plans-message {
          text-align: center;
          padding: 60px 20px;
          color: #666;
        }

        .no-plans-icon {
          font-size: 4rem;
          color: #ccc;
          margin-bottom: 20px;
        }

        .no-plans-message h3 {
          margin-bottom: 15px;
          color: #333;
        }

        .plan-card {
          background: white;
          border-radius: 20px;
          padding: 35px 25px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
          border: 2px solid #f0f0f0;
          position: relative;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
        }

        .plan-card.featured {
          border-color: #0066ff;
          transform: scale(1.02);
          box-shadow: 0 20px 60px rgba(0, 102, 255, 0.2);
        }

        .plan-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 25px 50px rgba(0,0,0,0.15);
        }

        .plan-card.featured:hover {
          transform: scale(1.02) translateY(-8px);
        }

        .popular-badge {
          position: absolute;
          top: -15px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(45deg, #0066ff, #0044cc);
          color: white;
          padding: 12px 25px;
          border-radius: 25px;
          font-weight: 700;
          font-size: 0.8rem;
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .savings-badge {
          position: absolute;
          top: 5px;
          right: 10px;
          background: #00cc88;
          color: white;
          padding: 8px 15px;
          border-radius: 15px;
          font-weight: 700;
          font-size: 0.8rem;
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .plan-header {
          text-align: center;
          margin-bottom: 25px;
          padding-bottom: 20px;
          border-bottom: 1px solid #f0f0f0;
        }

        .plan-type {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-bottom: 15px;
        }

        .plan-type-icon {
          font-size: 1.8rem;
          color: #ffbf00;
        }

        .plan-header h3 {
          font-size: 1.4rem;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0;
        }

        .price-section {
          margin-top: 15px;
        }

        .price {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 5px;
          margin-bottom: 8px;
        }

        .currency {
          font-size: 1.2rem;
          font-weight: 600;
          color: #1a1a1a;
        }

        .amount {
          font-size: 2.8rem;
          font-weight: 800;
          color: #0066ff;
          line-height: 1;
        }

        .period {
          font-size: 1.1rem;
          color: #666;
          font-weight: 600;
        }

        .original-price {
          text-decoration: line-through;
          color: #999;
          font-size: 1rem;
          margin-bottom: 5px;
        }

        .effective-price {
          color: #00cc88;
          font-weight: 700;
          font-size: 0.85rem;
        }

        .plan-features {
          flex: 1;
          margin-bottom: 25px;
        }

        .plan-features ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .plan-features li {
          display: flex;
          align-items: flex-start;
          padding: 12px 0;
          border-bottom: 1px solid #f8f9fa;
          font-size: 0.9rem;
        }

        .plan-features li.disabled {
          color: #999;
        }

        .feature-icon {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #00cc88;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.7rem;
          font-weight: 700;
          margin-right: 12px;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .feature-icon.disabled {
          background: #e0e0e0;
          color: #999;
        }

        .feature-icon.premium {
          background: linear-gradient(45deg, #0066ff, #0044cc);
        }

        .feature-text {
          line-height: 1.4;
        }

        .plan-action {
          text-align: center;
        }

        .subscribe-btn {
          width: 100%;
          padding: 16px 25px;
          border: none;
          border-radius: 12px;
          font-weight: 700;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-bottom: 12px;
        }

        .subscribe-btn:not(.btn-featured) {
          background: #0066ff;
          color: white;
        }

        .subscribe-btn:not(.btn-featured):hover {
          background: #0052cc;
          transform: translateY(-2px);
        }

        .btn-featured {
          background: linear-gradient(45deg, #0066ff, #0044cc);
          color: white;
          font-weight: 800;
        }

        .btn-featured:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(0, 102, 255, 0.4);
        }

        .btn-arrow {
          font-size: 1.1rem;
          transition: transform 0.3s ease;
        }

        .subscribe-btn:hover .btn-arrow {
          transform: translateX(5px);
        }

        .plan-guarantee {
          font-size: 0.8rem;
          color: #666;
          margin: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
        }

        .guarantee-icon {
          font-size: 0.9rem;
          color: #00cc88;
        }

        /* Value Proposition */
        .value-proposition {
          // padding: 80px 0;
          background: #f8f9fa;
          margin-bottom: 54px;
        }

        .value-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 40px;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .value-item {
          text-align: center;
          padding: 40px 25px;
          background: white;
          border-radius: 15px;
          box-shadow: 0 5px 25px rgba(0,0,0,0.08);
          transition: transform 0.3s ease;
        }

        .value-item:hover {
          transform: translateY(-5px);
        }

        .value-icon {
          font-size: 3rem;
          color: #0066ff;
          margin-bottom: 20px;
        }

        .value-item h4 {
          font-size: 1.3rem;
          font-weight: 700;
          margin-bottom: 15px;
          color: #1a1a1a;
        }

        .value-item p {
          color: #666;
          line-height: 1.6;
          font-size: 0.95rem;
        }

        /* Animations */
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .plans-container {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .subscription-hero h1 {
            font-size: 2.5rem;
          }

          .hero-stats {
            gap: 30px;
            flex-wrap: wrap;
          }

          .plans-container {
            grid-template-columns: 1fr;
          }

          .plan-card.featured {
            transform: none;
          }

          .plan-card.featured:hover {
            transform: translateY(-8px);
          }

          .section-header h2 {
            font-size: 2rem;
          }

          .amount {
            font-size: 2.5rem;
          }

          .value-grid {
            grid-template-columns: 1fr;
            gap: 25px;
          }
        }

        @media (max-width: 480px) {
          .subscription-hero h1 {
            font-size: 2rem;
          }

          .section-header h2 {
            font-size: 1.8rem;
          }

          .amount {
            font-size: 2.2rem;
          }

          .hero-stats {
            gap: 20px;
          }

          .stat-number {
            font-size: 2rem;
          }

          .stat-icon {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );

  return isLoggedIn ? (
    <MyProSidebarProvider>
      {mainContent}
    </MyProSidebarProvider>
  ) : (
    <div>
      {mainContent}
    </div>
  );
};

export default SubscriptionPlans;